<template>
  <header class="sticky top-0 z-30" style="background:var(--bg);">
    <div
      class="mx-auto flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5 md:px-7"
      style="max-width:var(--page-max);"
    >
      <AppBrand to="/app/dashboard" />

      <!-- nav -->
      <nav class="hidden items-center gap-0.5 md:flex">
        <NuxtLink
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="nav-link"
          :class="{ on: item.active }"
          :aria-current="item.active ? 'page' : undefined"
        >{{ item.label }}</NuxtLink>
      </nav>

      <div class="flex-1" />

      <ThemeToggle />

      <!-- avatar → settings -->
      <NuxtLink
        to="/app/settings"
        class="flex items-center justify-center"
        style="width:38px; height:38px; border-radius:50%; background:var(--raise); color:var(--ink); font-size:12px; font-weight:600;"
        :title="userStore.user?.email || 'Settings'"
      >{{ initials }}</NuxtLink>

      <AppButton icon aria-label="Sign out" title="Sign out" @click="signOut">
        <UIcon name="i-heroicons-arrow-right-on-rectangle" class="h-5 w-5" />
      </AppButton>

      <AppButton
        icon
        class="menu-btn"
        aria-label="Menu"
        :aria-expanded="mobileMenuOpen"
        @click="mobileMenuOpen = !mobileMenuOpen"
      >
        <UIcon name="i-heroicons-bars-3" class="h-5 w-5" />
      </AppButton>
    </div>
  </header>

  <!-- mobile menu -->
  <div
    v-if="mobileMenuOpen"
    class="flex flex-col md:hidden"
    style="border-bottom:1px solid var(--line); background:var(--panel);"
  >
    <NuxtLink
      v-for="item in navItems"
      :key="item.to"
      :to="item.to"
      class="px-4 py-3"
      :style="{ color: item.active ? 'var(--ink)' : 'var(--muted)', fontWeight: item.active ? 600 : 400 }"
      @click="mobileMenuOpen = false"
    >{{ item.label }}</NuxtLink>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import ThemeToggle from './ThemeToggle.vue'

const userStore = useUserStore()
const organizationsStore = useOrganizationsStore()
const { selectedOrgAlias } = storeToRefs(organizationsStore)
const router = useRouter()
const route = useRoute()
const mobileMenuOpen = ref(false)

type NavItem = { label: string; to: string; match: string }

// Dashboard covers every workspace. The rest belong to the workspace in the switcher.
const baseItems = computed<NavItem[]>(() => [
  { label: 'Dashboard', to: '/app/dashboard', match: '/app/dashboard' },
  ...(selectedOrgAlias.value
    ? [
        { label: 'Overview', to: `/app/workspaces/${selectedOrgAlias.value}`, match: '/app/workspaces' },
        { label: 'Emails', to: '/app/emails', match: '/app/emails' },
        { label: 'Settings', to: '/app/settings', match: '/app/settings' },
      ]
    : []),
])

const navItems = computed(() =>
  baseItems.value.map((item) => ({
    ...item,
    active: route.path === item.match || route.path.startsWith(item.match + '/'),
  })),
)

const initials = computed(() => {
  const email = userStore.user?.email
  if (!email) return 'LR'
  return email.slice(0, 2).toUpperCase()
})

async function signOut() {
  await userStore.signOut()
  router.push('/login')
}
</script>

<style scoped>
.nav-link {
  padding: 8px 12px;
  border-radius: 999px;
  color: var(--muted);
  font-weight: 500;
  transition: color 0.12s ease, background 0.12s ease;
}
.nav-link:hover {
  color: var(--ink);
}
.nav-link.on {
  background: var(--raise);
  color: var(--ink);
  font-weight: 600;
}
/* .btn is unlayered, so Tailwind's md:hidden cannot hide it. */
@media (min-width: 768px) {
  .menu-btn {
    display: none;
  }
}
</style>
